import React from 'react';
import assistantRouterChanges from './assistant-router.changes.md';

import { Stack } from '@mui/material';
import { HandleType, NodeToolbar, Position } from 'reactflow';
import { useNodeError } from '@common/hooks/useNodeError';
import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { useNodeOutline } from '@common/hooks/useNodeOutline';
import { NodeAnchors, NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { AssistantNodeChips } from '../Assistant/AssistantNodeChips';
import { ProviderModel } from './ProviderModel';
import {
  AssistantRouterNodeState,
  NodeConfig,
  NodeField,
  NodeFieldType,
  NodeInputType,
  NodeOutput,
  NodeType,
} from '../../';

const exampleFiles = [
  { type: 'image_url', image_url: { url: 'data:image/png;base64,...' } },
  { type: 'text', text: 'CSV file content' },
  { type: 'file', file: { filename: 'example.pdf', file_data: 'data:application/pdf;base64...' } },
];

export const config: NodeConfig = {
  version: '1.0',
  title: 'AI Assistant Router',
  identifier: 'AssistantRouter',
  command_id: 'assistant.router',
  background: '#3434EF',
  icon: 'router',
  description: 'Intelligently call the necessary assistants to answer a query.',
  changelog: assistantRouterChanges,
  anchors: [
    {
      label: 'Execute',
      id: 'assistant.router.call',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'assistant.router.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      label: 'Finished',
      id: 'assistant.router.finished',
      type: 'source' as HandleType,
      variant: 'execution',
    },
    {
      label: 'Data',
      id: 'assistant.router.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
  ],
};

export const state: AssistantRouterNodeState = {
  model: 'meta-llama/Llama-3.3-70B-Instruct',
  name: '',
  router_method: 'route_and_answer',
  rewrite_question: 'false',
  session_id: { type: NodeInputType.Expression, value: '' },
  include_session_tools: 'true',
  sender_id: { type: NodeInputType.Expression, value: '' },
  files: { type: NodeInputType.Expression, value: '' },
  query: { type: NodeInputType.Expression, value: '' },
  system: { type: NodeInputType.Expression, value: '' },
  assistants: [],
};

export const fields: NodeField[] = [
  {
    id: 'model',
    tab: '1',
    type: NodeFieldType.ModelWithTools,
    label: 'Model',
    description:
      'The large language model used to select the best assistant. Each assistant will be represented as a tool. Only models that support tool calling are available.',
    placeholder: 'openai/gpt-4o',
    required: true,
  },
  {
    id: 'name',
    tab: '1',
    type: NodeFieldType.Input,
    label: 'Name',
    display_name: true,
    description: 'Use this field to give your router a definitive name.',
    placeholder: 'Give your router a name',
    required: true,
  },
  {
    id: 'router_method',
    tab: '1',
    type: NodeFieldType.Option,
    label: 'Router Method',
    description:
      'Specify how the router will use selected assistants.\n\nRoute & Answer: The selected assistant will answer the question directly. This means only one assistant will be used for each input query.\n\nRoute & Return: The selected assistant will generate an answer and pass the response back to the router. The router can then run another assistant, or format an answer using the assistants response.',
    required: true,
    options: [
      { label: 'Route & Answer', value: 'route_and_answer' },
      { label: 'Route & Return', value: 'route_and_return' },
    ],
  },
  {
    id: 'session_id',
    tab: 'node_extra',
    type: NodeFieldType.InputExpression,
    label: 'Session ID',
    description:
      'Specify a unique identifier to enable chat history. Multiple request with the same session ID will be able to read the previous conversation history.',
    placeholder: '1234-1234-1234-1234',
  },
  {
    id: 'files',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Files',
    description: `An array of files using the message array file structure. Use a dynamic variable to pass an array of files.\n\n${JSON.stringify(
      exampleFiles,
      null,
      2
    )}`,
    placeholder: 'A dynamic variable containing an array of files',
  },
  {
    id: 'query',
    tab: '1',
    type: NodeFieldType.InputExpression,
    label: 'Query',
    description: 'This is the query that will be passed to the router on behalf of the customer.',
    placeholder: 'The users query',
  },
  {
    id: 'system',
    tab: '1',
    type: NodeFieldType.InputExpression,
    label: 'System Message',
    description:
      'Optionally define additional instructions for the router to use when selecting an assistant, or formatting an answer. This is useful for defining custom routing logic.',
    placeholder: 'Write a system message',
  },
  {
    id: 'include_session_tools',
    tab: '1',
    type: NodeFieldType.Toggle,
    label: 'Include Session Tools',
    description:
      'When enabled and using a session ID, the previous tool calls and results will be included in the conversation history.',
    options: [
      { label: 'Enabled', value: 'true' },
      { label: 'Disabled', value: 'false' },
    ],
  },
  {
    id: 'sender_id',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Sender ID',
    description:
      'Optionally specify an ID to identify the user sending the request. The ID will be present in the request reporting. Useful for multi-user applications.',
    placeholder: '1234-1234-1234-1234',
  },
  {
    id: 'assistants',
    tab: '1',
    type: NodeFieldType.AssistantRouter,
    label: 'Assistants',
    description:
      'Add assistants to this router. Each assistant will be selected when appropriate for each input query provided.',
    placeholder: 'Add an assistant',
  },
  {
    id: 'rewrite_question',
    tab: '1',
    type: NodeFieldType.Toggle,
    label: 'Rewrite Question',
    description:
      'When enabled, the LLM will rewrite the question after selecting an assistant to be more specific and accurate. This is useful when using Route & Return because the selected assistant will omit details from the question not specific to itself. For example, if the user asks two questions, each applicable assistant will only receive the part of the question that is relevant to them.',
    options: [
      { label: 'Enabled', value: 'true' },
      { label: 'Disabled', value: 'false' },
    ],
  },
];

export const output: NodeOutput = {
  default: {
    assistant: {
      router: {
        chatbot: 'workspace_id/workflow_id',
        type: 'http',
        user_id: 'user_id',
        message_id: 'message_id',
        session_id: null,
        query: 'query',
        answer: 'answer',
        system: 'system_message',
        model: 'gpt-4-32k',
        start: 86807.34609997272,
        prompt_tokens: 18,
        completion_tokens: 9,
        metadata: {
          tool_completions: [],
          documents: [],
          processing_duration: 0,
          show_help_action: false,
          show_feedback: false,
          error: null,
          result: 'llm_result',
        },
        response_format: {
          type: 'text',
        },
        save_thread: true,
      },
    },
  },
  dynamic: null,
};

export const AssistantRouter: React.FC<NodeType> = ({ id, selected, data }) => {
  const { title, background: _background, anchors, icon } = config;

  const themeType = useSelector((state: State) => state.flow.theme);
  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'assistant.router') return null;

  const nodeState: AssistantRouterNodeState = command.state;

  const model = nodeState?.model || '';
  const displayFieldId = command.fields.find((field) => field.display_name)?.id;
  const displayTitle = displayFieldId ? (nodeState[displayFieldId as keyof AssistantRouterNodeState] as string) : null;

  const background = themeType === 'dark' ? '#5858FF' : _background;
  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={displayTitle || title} icon={icon} background={background} status={nodeStatus} />
      {model && <ProviderModel model={model} />}
      <AssistantNodeChips assistantIds={nodeState.assistants} system={nodeState.system.value} />
      <NodeAnchors id={id} command={command} data={data} anchors={anchors} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
